import type { ChatMessage, Role } from "../types.ts";
import { getJSON, removeItem, setJSON } from "./storage.ts";
import { uid } from "./util.ts";

interface Persisted {
  conversationId: string | null;
  messages: ChatMessage[];
  updatedAt: number;
}

/** Lịch sử hiển thị cục bộ. Server có thể thay thế bằng bản đã lưu khi mở. */
export class Session {
  /** Khoá lưu tạm trong trình duyệt — TÁCH theo mã khách (`client`): đổi mã trên cùng trình duyệt ⛔ hiện lại
   *  hội thoại của kho khác (server cũng tách kho theo mã). Không mã = khoá cũ, như trước. */
  readonly key: string;
  private limit: number;
  private persist: boolean;

  conversationId: string | null = null;
  messages: ChatMessage[] = [];

  constructor(opts: { persist: boolean; limit: number; scope?: string }) {
    const scope = (opts.scope || "").trim().toLowerCase();
    this.key = scope ? "apus:assistant:" + scope : "apus:assistant";
    this.persist = opts.persist;
    this.limit = opts.limit;

    const saved = this.persist ? getJSON<Persisted>(this.key) : null;
    if (saved) {
      this.conversationId = saved.conversationId ?? null;
      this.messages = Array.isArray(saved.messages) ? saved.messages : [];
    }
  }

  setPersist(value: boolean): void {
    this.persist = value;
    if (!value) removeItem(this.key);
    else this.save();
  }

  add(role: Role, text: string, extra?: Partial<ChatMessage>): ChatMessage {
    const message: ChatMessage = { id: uid("m_"), role, text, at: Date.now(), ...extra };
    this.messages.push(message);
    if (this.messages.length > this.limit) this.messages.splice(0, this.messages.length - this.limit);
    this.save();
    return message;
  }

  update(id: string, patch: Partial<ChatMessage>): ChatMessage | undefined {
    const message = this.messages.find((m) => m.id === id);
    if (!message) return undefined;
    Object.assign(message, patch);
    this.save();
    return message;
  }

  remove(id: string): void {
    const index = this.messages.findIndex((m) => m.id === id);
    if (index >= 0) this.messages.splice(index, 1);
    this.save();
  }

  replace(messages: ChatMessage[]): void {
    const validRoles: Role[] = ["user", "assistant", "system"];
    this.messages = (Array.isArray(messages) ? messages : [])
      .filter(
        (message) =>
          !!message &&
          typeof message.id === "string" &&
          message.id.trim().length > 0 &&
          validRoles.includes(message.role) &&
          typeof message.text === "string" &&
          Number.isFinite(message.at),
      )
      .slice(-this.limit)
      .map((message) => ({ ...message, streaming: false }));
    this.save();
  }

  /** Câu hỏi gần nhất của người dùng — thứ nút Thử lại gửi lại. */
  lastQuestion(): string | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const message = this.messages[i];
      if (message && message.role === "user") return message.text;
    }
    return null;
  }

  clear(): void {
    this.messages = [];
    this.save();
  }

  reset(): void {
    this.messages = [];
    this.conversationId = null;
    this.save();
  }

  save(): void {
    if (!this.persist) return;
    /* Bong bóng đang stream dở không được lưu ở trạng thái "đang chạy" — tải
       lại tab sẽ thấy con trỏ nhấp nháy vĩnh viễn. */
    const messages = this.messages.map((m) => (m.streaming ? { ...m, streaming: false } : m));
    setJSON(this.key, {
      conversationId: this.conversationId,
      messages,
      updatedAt: Date.now(),
    } satisfies Persisted);
  }
}
